/**
 * Detects a stopped incoming RTP transport without treating quiet audio as a
 * failure. Natural sentence pauses continue to receive RTP packets; a broken
 * transport freezes both packet and byte counters.
 */
export class RealtimeTransportWatchdog {
    callbacks;
    suspectAfterMs;
    interruptAfterMs;
    baselineBytes = 0;
    baselinePackets = 0;
    lastProgressAtMs = 0;
    suspected = false;
    interrupted = false;
    constructor(callbacks, suspectAfterMs = 900, interruptAfterMs = 2_200) {
        this.callbacks = callbacks;
        this.suspectAfterMs = suspectAfterMs;
        this.interruptAfterMs = interruptAfterMs;
    }
    observe(sample) {
        if (!sample.outputActive) {
            this.reset();
            return;
        }
        const peerState = sample.peerConnectionState ?? "";
        const iceState = sample.iceConnectionState ?? "";
        if (peerState === "failed" || iceState === "failed") {
            this.interrupt("peer-failed");
            return;
        }
        const hasTransport = sample.bytesReceived > 0 || sample.packetsReceived > 0;
        const progressed = sample.bytesReceived > this.baselineBytes
            || sample.packetsReceived > this.baselinePackets;
        if (!hasTransport || progressed || this.lastProgressAtMs === 0) {
            const recovered = this.suspected && !this.interrupted && progressed;
            this.baselineBytes = Math.max(this.baselineBytes, sample.bytesReceived);
            this.baselinePackets = Math.max(this.baselinePackets, sample.packetsReceived);
            this.lastProgressAtMs = sample.atMs;
            this.suspected = false;
            if (recovered)
                this.callbacks.onRecovered?.();
            return;
        }
        const frozenForMs = Math.max(0, sample.atMs - this.lastProgressAtMs);
        if (!this.suspected && frozenForMs >= this.suspectAfterMs) {
            this.suspected = true;
            this.callbacks.onSuspected?.();
        }
        if (this.interrupted)
            return;
        if (frozenForMs < this.interruptAfterMs)
            return;
        this.interrupt(peerState === "disconnected" || iceState === "disconnected"
            ? "peer-disconnected"
            : "incoming-audio-stalled");
    }
    reset() {
        this.baselineBytes = 0;
        this.baselinePackets = 0;
        this.lastProgressAtMs = 0;
        this.suspected = false;
        this.interrupted = false;
    }
    interrupt(reason) {
        this.interrupted = true;
        this.callbacks.onInterrupted(reason);
    }
}
